import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { currentDomain, currentPath } from '../Index';
import { usePromotionsData } from '../data/PromotionData';
import LoginGlobal from '../data/LoginGlobal';
import { useNavigate } from 'react-router-dom';
import useAppPromotionTheme from '../hooks/useAppPromotionTheme';
import PromotionSkeletonDetails from '../data/PromotionSkeletonDetails';

export default function VIPReferralProgram({ isLightMode }) {
    const isAppPromo = useAppPromotionTheme(isLightMode);
    const { promotionsData, isPromotionDataReady } = usePromotionsData();
    const data = useMemo(
        () =>
            promotionsData.find(
                (promo) => promo.title === 'VIP Referral Program'
            ),
        [promotionsData]
    );
    const [isMobile, setIsMobile] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const loggedInState = localStorage.getItem('isLoggedIn') === 'true';
        setIsLoggedIn(loggedInState);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 841);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Table Data Incase
    const tableData = [
        {
            vipTier: 'Alexandrite',
            commission: '19%',
        },
        {
            vipTier: 'Diamond',
            commission: '18%',
        },
        {
            vipTier: 'Ruby',
            commission: '17%',
        },
        {
            vipTier: 'Sapphire',
            commission: '16%',
        },
        {
            vipTier: 'Emerald',
            commission: '15%',
        },
    ];

    // Only redirect when promotion data is ready
    // and the promotion genuinely does not exist.
    useEffect(() => {
        if (!isPromotionDataReady) return;

        if (!data) {
            navigate('/not-found', { replace: true });
        }
    }, [isPromotionDataReady, data, navigate]);

    // Show skeleton while waiting for server time.
    if (!isPromotionDataReady) {
        return <PromotionSkeletonDetails />;
    }

    // Avoid displaying incomplete promotion details
    // while redirecting to the not-found page.
    if (!data) {
        return null;
    }

    return (
        <>
            <Helmet>
                <meta charSet="utf-8" />
                <title>VIP Referral Program</title>
                <link rel="icon" href="/images/fav.webp" sizes="32x32" />
                <meta
                    name="description"
                    content="Earn up to 19% referral commission - Exclusive for FGH VIPs"
                />
                <link rel="canonical" href={`${currentDomain}${currentPath}`} />
            </Helmet>
            <div className="promotion-page-main all-page-main-container-min-height">
                <div className="promotion-page-container main-container-global-adjusted-padding">
                    <div className="promotion-tnc-page">
                        <div className="promotion-page-breadcrumb">
                            <a
                                href={
                                    isAppPromo
                                        ? '/promotion/'.replace(
                                              '/promotion',
                                              '/app-promotion'
                                          )
                                        : '/promotion/'
                                }
                            >
                                Promotions{' '}
                            </a>{' '}
                            / <span>{data.title}</span>
                        </div>
                        <div className="promotion-tnc-banner">
                            <img
                                src={isMobile ? data.imgSrc : data.bannerSrc}
                                alt={`FGH ${data.title}`}
                            />
                        </div>
                        <div className="promotion-tnc-content">
                            <div className="promotion-tnc-info">
                                <div
                                    className={`promotion-indication-message ${data.promotionMessage.class}`}
                                >
                                    {data.promotionMessage.text}
                                </div>
                                <h1>VIP Referral Program</h1>
                                <h2>Earn up to 19% Referral Commission</h2>

                                <div className="promotion-tnc-period">
                                    <strong>Promo Period: </strong>
                                    <span>Weekly Promotion</span>
                                </div>
                            </div>

                            <div className="promotion-tnc-mechanic-content">
                                <div className="table-wrapper">
                                    <table
                                        className="promotion-tnc-table"
                                        style={{ minWidth: '0px' }}
                                    >
                                        <thead>
                                            <tr>
                                                <th>VIP Tier</th>
                                                <th>Referral Commission</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tableData.map((row, index) => (
                                                <tr key={index}>
                                                    <td>{row.vipTier}</td>
                                                    <td>{row.commission}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <strong>Terms & Conditions:</strong>

                                <ol>
                                    <li>
                                        <p>
                                            Referrer must have at least 4 active
                                            referred players per week.
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            Referred players must have minimum
                                            total deposit of PHP 1000 per cut
                                            off to consider active referral.
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            Minimum of 10,000 losses from your
                                            referrals inorder to qualify.
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            Referral commission is based on the
                                            referred players’ total losses per
                                            week (Monday 12pm – Monday 11:59am)
                                            and the referrer’s VIP Level (%).
                                        </p>
                                        <p>
                                            <span className="promotion-tnc-block-text">
                                                (Example : Referred players
                                                total losses: Php 50,000 Earned
                                                referral commission: Php 50,000
                                                x 19% (Referrer’s VIP Level is
                                                ALEXANDRITE) = Php 9,500)
                                            </span>
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            Referral commission will be
                                            automatically credited to referrer’s
                                            FGH Balance every Monday 3:00
                                            PM(GMT+8).
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            Referred players cannot have the
                                            same IP/bank account with their
                                            referrer. Duplicate or Shared
                                            User/IP will result in
                                            disqualification and will not
                                            receive any referral commission.
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            Referrer cannot have previous
                                            account or more than one account
                                            with us.
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            Wagering requirement for referral
                                            bonus is 3X rollover before any
                                            withdrawal and can be played on ALL
                                            GAMES.
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            We reserve the right to change the
                                            terms of the referral program at any
                                            time, risk of fraud or any other
                                            factors deemed relevant.
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            The referrer will receive a regular
                                            5% referral commission with no
                                            minimum losses from their referred
                                            players if they have less than 4
                                            active players each week and the
                                            referred players' total deposit is
                                            less than PHP1000.
                                        </p>
                                    </li>
                                    <li>
                                        <p>
                                            <strong className="highlight">
                                                {' '}
                                                Unused Bonus Reset Policy:
                                            </strong>
                                        </p>
                                        <p>
                                            <span className="promotion-tnc-block-text unused-bonus-reset">
                                                &bull; Once the bonus is
                                                credited to the player’s
                                                account, it must be used before{' '}
                                                <strong className="highlight">
                                                    {' '}
                                                    11:00 AM on the first day of
                                                    the following month.{' '}
                                                </strong>{' '}
                                                Any unused bonus balance after
                                                this period will automatically
                                                reset and be forfeited.
                                            </span>
                                        </p>
                                    </li>
                                </ol>
                                {!isAppPromo && (
                                    <div className="promotion-tnc-cta">
                                        {isLoggedIn ? (
                                            <a
                                                href="/profile/#referral"
                                                className="promotion-cta-btn"
                                            >
                                                Refer a Friend
                                            </a>
                                        ) : (
                                            <LoginGlobal
                                                label="Refer a Friend"
                                                btnClass="promotion-cta-btn"
                                                onlyBtn={true}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
